import React from 'react';
import { useSelector } from 'react-redux';
import { segment } from '@actions/analytics';
import { evtNameCategoryMap } from '@constants/index';
import {
  CLX_LESSON_VIEW,
  SYLLABUS,
  SYLLABUS_CLICKED_KEYS,
  TOPIC_SYLLABUS_PAGE
} from '@constants/segment';
import useCheckFeatureLimit, {
  FeatureObjectType
} from '@cont/DeviceLimitation/useCheckFeatureLimit';
import { getEventMeta } from '@utils/cmsEventDedup';
import useFetch from '@hooks/useFetch';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { getIsSubscribedToGoal } from '@selectors/subscription';
import { getClassInfoEndpoint } from '@cont/Syllabus/constants/apiHelper';
import { processClassData } from '@api/hooks/class/utils';
import useCourseClassPlayer from '@hooks/useCourseClassPlayer';

interface IFetchAndRenderProps {
  uid: string;
  goalUID: string;
  closeCLXInterface: React.Dispatch<React.SetStateAction<boolean>>;
  isDemoVideo: boolean;
  disableTopicPage?: (value: boolean) => void;
  isSyllabusPageEducatorSpecific: boolean;
  source?: string;
}
const FetchAndRender: React.FC<IFetchAndRenderProps> = ({
  uid,
  goalUID,
  closeCLXInterface,
  isDemoVideo,
  disableTopicPage = () => {},
  isSyllabusPageEducatorSpecific,
  source
}) => {
  const endPoint = getClassInfoEndpoint(uid, goalUID, true);
  const { data, isLoading, isError } = useFetch(endPoint, {
    headers: {
      'X-Direct-To': 'sheldon',
      'X-PAGE': 'CLX'
    },
    onError: (err) => {
      segment.track('INFO_V2_ERROR', {
        fileRef: 'fetchAndRender',
        apiUrl: endPoint,
        error: err
      });
    }
  });
  const isSubscribed = useSelector(getIsSubscribedToGoal(goalUID));
  const goalSegmentData = useGoalEventProps({ goalUID });
  const { open: openClassPlayer } = useCourseClassPlayer();

  const isFreeLesson = !isSubscribed;

  const handleClassItemClick = useCheckFeatureLimit(
    FeatureObjectType.CLASS,
    () => {
      const classData = processClassData(data);

      openClassPlayer({
        lesson: classData,
        course: classData.programme,
        byPassCheck: true,
        isFreeLesson,
        forceFetch: !isFreeLesson,
        source
      });

      disableTopicPage(false);
    }
  );

  React.useEffect(() => {
    if (data?.uid && !isLoading) {
      handleClassItemClick();
      {
        const trackLessonView = async () => {
          const { send_to_crm } = await getEventMeta(CLX_LESSON_VIEW, {
            goal_uid: goalSegmentData?.goal_uid
          });
          segment.track(
            CLX_LESSON_VIEW,
            {
              ...goalSegmentData,
              is_demo: isDemoVideo,
              last_primary_source: isSyllabusPageEducatorSpecific
                ? 'Educator Profile'
                : TOPIC_SYLLABUS_PAGE,
              last_primary_source_section: isSyllabusPageEducatorSpecific
                ? 'Educator Profile'
                : TOPIC_SYLLABUS_PAGE,
              path: isSyllabusPageEducatorSpecific
                ? ['Educator Profile']
                : [SYLLABUS],
              send_to_crm
            },
            evtNameCategoryMap.WITHOUT_CATEGORY,
            true,
            SYLLABUS_CLICKED_KEYS
          );
        };
        trackLessonView();
      }
      closeCLXInterface(false);
    }
    if (!isLoading && isError) {
      disableTopicPage(false);
      closeCLXInterface(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.uid, isLoading]);

  return null;
};

export default FetchAndRender;
